import { ApolloError } from "@apollo/client";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { AnalyticsResultDto, AndFilter, AnyFilterExpressionInput, Attribute, AttributeType, BooleanOp, DeviceMeasurement, Entry_String_AttributeData_String, Field, FilterOp, MdmDeviceDto, MdmObjectDto, MdmObjectJobDto, MdmProjectConfiguration, SortDirection } from "gql/generated/types";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import React from "react";
import { FieldErrors, FieldValues, Path } from "react-hook-form";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { StringKey } from "services/lang/keys";
import { hasDecimals } from "utils/utils";
import { v4 as uuid } from "uuid";
import DeviceStatusTag from "../components/DeviceStatus";
import { priorityType } from "./const";
import { CustomRowData, FilterInternal, MdmTableName } from "./types";

export const getFormErrorMessage = (name: keyof FieldValues, errors: FieldErrors<FieldValues>) => {
  return errors[name] ? (
    <small className="p-error">{errors[name]?.message as string}</small>
  ) : (
    <small className="p-error">&nbsp;</small>
  );
};

export const successToaster = (msg: string) =>
  easasAlert({
    severity: "success",
    summary: T("alert.success"),
    detail: msg,
    life: 2000,
  });

export const errorToaster = (msg: string) => {
  easasAlert({ severity: "error", summary: T("alert.error"), detail: msg, life: 3000 });
};

export const apolloErrorToaster = (err: ApolloError) => {
  easasAlert({ severity: "error", summary: T("alert.error"), detail: err.message, life: 3000 });
};

// -------------------------------------- Table Row Templates --------------------------------------

export const severityRowFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
  return (
    <Dropdown
      value={options.value}
      options={Object.entries(priorityType).map(([key, value]) => ({
        label: value.value,
        value: key,
      }))}
      onChange={e => options.filterApplyCallback(e.value)}
      placeholder={T("word.select-one")}
      showClear
    />
  );
};

export const renderSeverity = (rowData: MdmObjectJobDto | AnalyticsResultDto) => (
  <i
    className={priorityType[rowData.severity!].icon}
    style={{ color: priorityType[rowData.severity!].severity }}
    title={priorityType[rowData.severity!].value}
  >
    <span className="pl-2 text-gray-900 p-component">{priorityType[rowData.severity!].value}</span>
  </i>
);

export const renderDeviceStatus = (rowData?: MdmDeviceDto | null) => (
  <DeviceStatusTag
    status={rowData?.status.status}
    lastMeasurementDate={rowData?.status.lastMeasurementDate}
  />
);

export const renderObjectLinkInAnalytics = (rowData: AnalyticsResultDto, projectId: string) => (
  <Link
    to={`/mdm/${projectId}/objects/${rowData.mdmObject?.id}`}
    className="truncate-text block"
    title={rowData.mdmObject?.title ?? ""}
  >
    {rowData.mdmObject?.title}
  </Link>
);

export const renderObjectLinkInDevices = (rowData: MdmDeviceDto, projectId: string) => (
  <Link to={`/mdm/${projectId}/objects/${rowData.objectRelation?.mdmObject?.id}`}>
    {rowData.objectRelation?.mdmObject?.identifier}
  </Link>
);

export const renderDeviceLink = (rowData: AnalyticsResultDto, projectId: string) => (
  <Link to={`/mdm/${projectId}/devices/${rowData.mdmDevice?.id}`}>
    {rowData.mdmDevice?.identifier}
  </Link>
);

export const rowDropdownFilterTemplate = (
  options: ColumnFilterElementTemplateOptions,
  dropdownOptions: any[]
) => {
  return (
    <Dropdown
      value={options.value}
      options={dropdownOptions}
      onChange={e => options.filterApplyCallback(e.value)}
      placeholder={T("word.select-one")}
      showClear
    />
  );
};

export const renderLastMeasurementValue = (data: MdmDeviceDto, key: string) => {
  const value = (data.lastMeasurements as unknown as Record<string, number>)[key];
  if (hasDecimals(value)) {
    return key === "Darbo valandos" ? value.toFixed(0) : value.toFixed(2);
  }
  return value ?? "-";
};

export const renderCustomFieldValue = (
  rowData: CustomRowData,
  id: keyof CustomRowData,
  customColumns: Attribute[]
) => {
  const customAttribute = customColumns.filter(col => col?.id === id)[0];
  if (
    id &&
    customAttribute?.type === AttributeType.Link &&
    rowData[id]?.value &&
    customAttribute.reading
  ) {
    return (
      <a
        href={customAttribute.reading?.replace("{id}", rowData[id]?.value?.toString()!)}
        rel="noreferrer"
        target="_blank"
      >
        {rowData[id]?.value}
      </a>
    );
  }
  return rowData[id]?.value ?? "-";
};

// -------------------------------------- Filter Templates --------------------------------------

export const searchAnalyticsFilter = (value: string): AnyFilterExpressionInput => ({
  op: BooleanOp.Or,
  left: {
    op: BooleanOp.Or,
    left: {
      op: BooleanOp.Or,
      left: {
        value,
        cond: FilterOp.Contains,
        property: "description",
      },
      right: {
        value,
        cond: FilterOp.Contains,
        property: "mdmObject.address.fullAddress",
      },
    },
    right: {
      op: BooleanOp.Or,
      left: {
        value,
        cond: FilterOp.Contains,
        property: "device.identifier",
      },
      right: {
        value,
        cond: FilterOp.Contains,
        property: "mdmObject.identifier",
      },
    },
  },
  right: {
    op: BooleanOp.Or,
    left: {
      value,
      cond: FilterOp.Contains,
      property: "deviceType",
    },
    right: {
      value,
      cond: FilterOp.Contains,
      property: "severity",
    },
  },
});

export const searchDeviceOrObjectFilter = (
  value: string,
  tableName: MdmTableName
): AnyFilterExpressionInput => ({
  op: BooleanOp.Or,
  left: {
    op: BooleanOp.Or,
    left: {
      value,
      cond: FilterOp.Contains,
      property:
        tableName === "Devices"
          ? "objectRelation.mdmObject.address.fullAddress"
          : "address.fullAddress",
    },
    right: {
      value,
      cond: FilterOp.Contains,
      property: tableName === "Devices" ? "config.title" : "title",
    },
  },
  right: {
    value,
    cond: FilterOp.Contains,
    property: "identifier",
  },
});

// -------------------------------------- Utils --------------------------------------

type NestedField = Field & { children?: NestedField[]; data: Field[]; property: string };

export const buildNestedFields = (
  containerType: string,
  fields: Field[] = [],
  key: string = "",
  data: Field[] = []
): NestedField[] => {
  return fields
    .filter(field => field.containerType === containerType)
    .map((field: Field, i) => {
      const k = key === "" ? `${i}` : `${key}-${i}`;
      if (field.leaf) {
        return {
          ...field,
          label: T(field.label as StringKey, { defaultValue: field.label }),
          data: [...data, field],
          key: k,
          property: field.key,
        };
      }
      return {
        ...field,
        label: T(field.label as StringKey, { defaultValue: field.label }),
        property: field.key,
        data: [...data, field],
        key: k,
        children: buildNestedFields(field.fieldType!, fields, k, [...data, field]),
      };
    });
};

export const formatAndFilter = (
  andFilters: AndFilter[] | undefined,
  treeOptions: NestedField[]
): FilterInternal[] => {
  return (
    andFilters?.map((f: AndFilter) => {
      const fields = findByPropertyPath(treeOptions, f.property)?.data ?? [];
      return {
        cond: f.cond,
        fields,
        value: f.value?.includes("|") ? f.value?.split("|")! : f.value!,
        id: uuid(),
        label: fields[0]?.label ?? "",
      };
    }) ?? []
  );
};

export const findObjectValue = <T,>(object: T, key: Path<T> | undefined): unknown => {
  if (key && key.includes(".")) {
    const [key1, ...restKeys] = key.split(".");
    if (Object.prototype.hasOwnProperty.call(object, "mdmObject")) {
      const value = findObjectValue(
        (object as { mdmObject: MdmObjectDto }).mdmObject,
        key as Path<MdmObjectDto>
      );
      if (value) return value;
    }
    if (Object.prototype.hasOwnProperty.call(object, "mdmDevice")) {
      const value = findObjectValue(
        (object as { mdmDevice: MdmDeviceDto }).mdmDevice,
        key as Path<MdmDeviceDto>
      );
      if (value) return value;
    }
    if (Object.prototype.hasOwnProperty.call(object, "lastMeasurements")) {
      const objectVal = (
        object as { lastMeasurements: DeviceMeasurement[] }
      ).lastMeasurements?.find(item => item.mapping === restKeys[0])?.value;
      if (objectVal) return objectVal;
    }
    if (Object.prototype.hasOwnProperty.call(object, "customFields")) {
      const objectVal = (
        object as { customFields: Entry_String_AttributeData_String[] }
      ).customFields?.find(item => item.key === key1)?.value?.value;
      if (objectVal) return objectVal;
    }
    if (!object[key1 as keyof T]) return null;
    return findObjectValue(object[key1 as keyof T], restKeys.join(".") as Path<T[keyof T]>);
  }
  return object[key as unknown as keyof T];
};

export const findByKey = (data: NestedField[], key: string): NestedField | null => {
  // eslint-disable-next-line no-restricted-syntax
  for (const field of data) {
    if (field.key === key) {
      return field;
    }
    if (field.children && field.children.length > 0) {
      const found = findByKey(field.children, key);
      if (found) return found;
    }
  }
  return null;
};

export const findByPropertyPath = (
  data: NestedField[],
  propertyPath: string
): NestedField | null => {
  // eslint-disable-next-line no-restricted-syntax
  for (const field of data) {
    if (field.property === propertyPath) {
      return field;
    }
    let paths = propertyPath.split(".");
    if (paths[0] === "customFields") {
      paths = [`${paths[0]}.${paths[1]}`, ...paths.slice(2)];
    }
    if (field.property === paths[0] && field.children && field.children.length > 0) {
      const found = findByPropertyPath(field.children, paths.slice(1).join("."));
      if (found) return found;
    }
  }
  return null;
};

export const formatSort = (
  sortField: string,
  sortOrder: number | undefined | null,
  tableName: MdmTableName,
  config: MdmProjectConfiguration | undefined | null
) => {
  let property = sortField!;
  const properties = sortField.split(".");
  if (tableName === "Devices" || tableName === "Objects") {
    property =
      properties.length === 2 && properties.at(-1) === "value"
        ? `customFields.${properties[0]}`
        : sortField!;
  }
  if (
    tableName.startsWith("Analytics") &&
    properties.length === 2 &&
    properties.at(-1) === "value"
  ) {
    if (config?.deviceConfiguration.customFields.find(f => f?.id === properties[0])) {
      property = `device.customFields.${properties[0]}`;
    }
    if (config?.objectConfiguration.customFields.find(f => f?.id === properties[0])) {
      property = `mdmObject.customFields.${properties[0]}`;
    }
  }
  return [{ property, direction: sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc }];
};

export const measurementUnits = (key: string) => {
  switch (key) {
    case "D":
      return "l/h";
    case "P":
      return "kW";
    case "Q":
      return "kWh";
    case "V":
      return "m³";
    case "T1":
    case "T2":
      return "°C";
    default:
      return null;
  }
};

export const multiselectValue = (value: string[] | string | null) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : null;
};
